// Split (sheet, section 8; Spencer, 2026-10-19): a client's rooms start deciding separately, so the opportunity splits
// into two, each keeping its own jobs, its step and its share of the activity log, with a link back. Pick the name, the
// jobs that go, and the log entries that go with them.

import { useState } from 'react';
import type { Row } from '../../lib/api';
import { monthDay } from '../../lib/format';
import { splitOpportunity, type Lead, type Pipeline } from '../../lib/pipeline';
import { Btn, Modal } from '../../ui/kit';

export function SplitDialog({ p, lead, log, onClose, onSplit }: { p: Pipeline; lead: Lead; log: Row[]; onClose: () => void; onSplit: (newId: string) => void }) {
  const base = lead.name.includes(' · ') ? lead.name.split(' · ')[0] : lead.name.split(' - ')[0] ?? lead.name;
  const [name, setName] = useState(`${base} · `);
  const [jobs, setJobs] = useState<Set<string>>(new Set());
  const [entries, setEntries] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (set: Set<string>, id: string, put: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    put(next);
  };
  void p;
  const go = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      const id = await splitOpportunity(lead, name.trim(), [...jobs], [...entries]);
      onClose();
      onSplit(id);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Split ${lead.name}`} onClose={onClose}>
      <div className="stack">
        <p className="muted">
          The new opportunity starts on the same stage and step, with the same contacts, answers and dates. The jobs and log entries you tick move to it; the rest stay here. Nothing is deleted, and no estimate changes.
        </p>
        <label className="field">
          <span>Name of the new opportunity</span>
          <input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="3721 Schoeberl · Laundry" />
        </label>
        <div>
          <div className="lb">Jobs that move</div>
          {lead.jobs.length === 0 && <small>No jobs on this opportunity.</small>}
          {lead.jobs.map((j) => (
            <label key={j.job.id} className="check" style={{ display: 'flex', padding: '4px 0' }}>
              <input type="checkbox" checked={jobs.has(j.job.id)} onChange={() => toggle(jobs, j.job.id, setJobs)} /> {j.job.scope_name}
            </label>
          ))}
        </div>
        <div>
          <div className="lb">Log entries that move</div>
          <div style={{ maxHeight: 240, overflow: 'auto' }}>
            {log.map((a) => (
              <label key={a.id} className="check" style={{ display: 'flex', padding: '3px 0', alignItems: 'flex-start' }}>
                <input type="checkbox" checked={entries.has(a.id)} onChange={() => toggle(entries, a.id, setEntries)} />
                <small>
                  {monthDay(a.at)} · {a.kind} · {String(a.body ?? '').replace(/\*\*/g, '').slice(0, 90)}
                </small>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Btn primary disabled={busy || !name.trim()} onClick={() => void go()}>
            Split into two
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
